import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Archive,
  BarChart3,
  CalendarClock,
  CheckCircle2,
  Copy,
  Facebook,
  Globe2,
  Instagram,
  Linkedin,
  Mail,
  Megaphone,
  MessageSquareText,
  Plus,
  RefreshCw,
  Rocket,
  Search,
  Send,
  Settings2,
  Sparkles,
  Target,
  Trash2,
  Wand2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import {
  bulkCreateMarketingContent,
  createMarketingCampaign,
  deleteMarketingCampaign,
  deleteMarketingContent,
  generateMarketingContent,
  updateMarketingAiSettings,
  generateCampaignPosts,
  getMarketingCampaigns,
  getMarketingContent,
  getMarketingAiSettings,
  getMarketingOverview,
  shareMarketingContent,
  updateMarketingCampaign,
  updateMarketingContent,
} from '@/lib/api';

const CHANNELS = [
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'instagram', label: 'Instagram', icon: Instagram },
  { id: 'facebook', label: 'Facebook', icon: Facebook },
  { id: 'linkedin', label: 'LinkedIn', icon: Linkedin },
  { id: 'google_ads', label: 'Google Ads', icon: Search },
  { id: 'meta_ads', label: 'Meta Ads', icon: Megaphone },
  { id: 'website', label: 'Website', icon: Globe2 },
  { id: 'blog', label: 'Blog', icon: MessageSquareText },
  { id: 'sms', label: 'SMS', icon: MessageSquareText },
  { id: 'whatsapp', label: 'WhatsApp', icon: Send },
];

const CHANNEL_LABELS = CHANNELS.reduce((acc, channel) => {
  acc[channel.id] = channel.label;
  return acc;
}, {});

const OBJECTIVES = [
  { value: 'lead_generation', label: 'Lead generation' },
  { value: 'enrollment', label: 'Enrollment' },
  { value: 'brand_awareness', label: 'Brand awareness' },
  { value: 'event_registration', label: 'Event registration' },
  { value: 'retargeting', label: 'Retargeting' },
];

const TONES = [
  { value: 'professional', label: 'Professional' },
  { value: 'warm', label: 'Warm' },
  { value: 'bold', label: 'Bold' },
  { value: 'student_friendly', label: 'Student friendly' },
  { value: 'premium', label: 'Premium' },
];

const CONTENT_STATUSES = ['draft', 'review', 'scheduled', 'shared', 'archived'];
const CAMPAIGN_STATUSES = ['draft', 'active', 'paused', 'completed'];

const buildDefaultGeneratorForm = () => ({
  campaign_name: 'Fall Enrollment Push',
  objective: 'lead_generation',
  audience: 'prospective international students',
  program: 'Business Administration Diploma',
  tone: 'professional',
  offer: 'personal admissions guidance',
  location: 'Vancouver, BC',
  budget: '2500',
  channels: ['email', 'instagram', 'facebook', 'google_ads', 'website'],
});

const buildDefaultCampaignForm = () => ({
  name: '',
  objective: 'lead_generation',
  audience: 'Prospective students',
  program: '',
  budget: '0',
  channels: ['email', 'instagram', 'google_ads'],
  status: 'draft',
  start_date: '',
  end_date: '',
  notes: '',
});

const buildDefaultPostForm = () => ({
  campaign_id: '',
  post_count: '6',
  tone: 'student_friendly',
  media_type: 'image',
  offer: 'Personal admissions guidance',
  location: 'Vancouver, BC',
});

function titleCase(value) {
  return String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatMoney(value) {
  const amount = Number(value || 0);
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(value) {
  if (!value) return 'Not scheduled';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function channelLabel(channel) {
  return CHANNEL_LABELS[channel] || titleCase(channel);
}

function statusBadgeClass(status) {
  const classes = {
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    scheduled: 'bg-blue-50 text-blue-700 border-blue-200',
    shared: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    review: 'bg-amber-50 text-amber-700 border-amber-200',
    paused: 'bg-amber-50 text-amber-700 border-amber-200',
    archived: 'bg-slate-100 text-slate-600 border-slate-200',
    completed: 'bg-slate-100 text-slate-600 border-slate-200',
    draft: 'bg-[#F7F8FA] text-[#062B1A]/70 border-[#062B1A]/10',
  };
  return classes[status] || classes.draft;
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/50">{label}</span>
          <Icon className="h-4 w-4 text-[#D4AF37]" />
        </div>
        <p className="mt-3 text-3xl font-bold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
          {value}
        </p>
      </CardContent>
    </Card>
  );
}

function ChannelPicker({ value, onChange }) {
  const selected = value || [];

  const handleToggle = (channelId, checked) => {
    if (checked) {
      onChange(selected.includes(channelId) ? selected : [...selected, channelId]);
      return;
    }
    if (selected.length <= 1) return;
    onChange(selected.filter((item) => item !== channelId));
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
      {CHANNELS.map((channel) => {
        const Icon = channel.icon;
        const checked = selected.includes(channel.id);
        return (
          <label
            key={channel.id}
            className={`flex min-h-11 items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
              checked ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#062B1A]' : 'border-[#062B1A]/10 bg-white text-[#062B1A]/70'
            }`}
          >
            <Checkbox checked={checked} onCheckedChange={(next) => handleToggle(channel.id, Boolean(next))} />
            <Icon className="h-4 w-4 flex-shrink-0" />
            <span className="min-w-0 truncate">{channel.label}</span>
          </label>
        );
      })}
    </div>
  );
}

function ChannelBadge({ channel }) {
  return (
    <Badge variant="outline" className="border-[#062B1A]/10 bg-white text-[#062B1A]/70">
      {channelLabel(channel)}
    </Badge>
  );
}

function ContentPreview({ item, campaignName, onCopy, onQueue, onShare, onArchive, onDelete }) {
  return (
    <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
      <CardContent className="p-4">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <ChannelBadge channel={item.channel} />
              <Badge variant="outline" className={statusBadgeClass(item.status)}>
                {titleCase(item.status)}
              </Badge>
              {item.metadata?.media_type && item.metadata.media_type !== 'none' && (
                <Badge variant="outline" className="border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#8A6A12]">
                  {titleCase(item.metadata.media_type)} · {titleCase(item.metadata.media_status)}
                </Badge>
              )}
              {campaignName && (
                <span className="text-xs font-medium text-[#062B1A]/45">{campaignName}</span>
              )}
            </div>
            <h3 className="mt-3 text-base font-semibold text-[#062B1A]">{item.title}</h3>
            {item.headline && <p className="mt-1 text-sm font-medium text-[#062B1A]/75">{item.headline}</p>}
            <p className="mt-3 whitespace-pre-line text-sm leading-6 text-[#062B1A]/65">{item.body || item.caption}</p>
            {item.hashtags?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {item.hashtags.map((tag) => (
                  <span key={tag} className="text-xs font-medium text-[#062B1A]/45">
                    {tag}
                  </span>
                ))}
              </div>
            )}
            {item.asset_prompt && (
              <div className="mt-3 rounded-lg border border-[#062B1A]/10 bg-[#F7F8FA] p-3 text-xs leading-5 text-[#062B1A]/55">
                {item.asset_prompt}
              </div>
            )}
            <p className="mt-3 text-xs text-[#062B1A]/40">Scheduled: {formatDate(item.scheduled_for)}</p>
          </div>
          <div className="flex flex-wrap gap-2 xl:w-[340px] xl:justify-end">
            <Button variant="outline" size="sm" onClick={() => onCopy(item)}>
              <Copy className="h-3.5 w-3.5" />
              Copy
            </Button>
            {item.status !== 'scheduled' && item.status !== 'shared' && item.status !== 'archived' && (
              <Button variant="outline" size="sm" onClick={() => onQueue(item)}>
                <CalendarClock className="h-3.5 w-3.5" />
                Queue
              </Button>
            )}
            {item.status !== 'shared' && item.status !== 'archived' && (
              <Button size="sm" onClick={() => onShare(item)}>
                <Send className="h-3.5 w-3.5" />
                Share
              </Button>
            )}
            {item.status !== 'archived' && (
              <Button variant="outline" size="sm" onClick={() => onArchive(item)}>
                <Archive className="h-3.5 w-3.5" />
                Archive
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => onDelete(item)}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminMarketing() {
  const { loading: authLoading } = useAuth();
  const [overview, setOverview] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [contentItems, setContentItems] = useState([]);
  const [generated, setGenerated] = useState(null);
  const [generatorForm, setGeneratorForm] = useState(buildDefaultGeneratorForm);
  const [campaignForm, setCampaignForm] = useState(buildDefaultCampaignForm);
  const [postForm, setPostForm] = useState(buildDefaultPostForm);
  const [contentFilters, setContentFilters] = useState({ status: 'all', channel: 'all' });
  const [activeTab, setActiveTab] = useState('studio');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [aiSettings, setAiSettings] = useState(null);
  const [settingsForm, setSettingsForm] = useState(null);

  const campaignMap = useMemo(() => {
    return campaigns.reduce((acc, campaign) => {
      acc[campaign.id] = campaign.name;
      return acc;
    }, {});
  }, [campaigns]);

  const activeModel = useMemo(() => {
    return (aiSettings?.model_catalog || []).find((entry) => entry.id === settingsForm?.model) || null;
  }, [aiSettings, settingsForm]);

  const filteredContent = useMemo(() => {
    return contentItems.filter((item) => {
      const statusMatch = contentFilters.status === 'all' || item.status === contentFilters.status;
      const channelMatch = contentFilters.channel === 'all' || item.channel === contentFilters.channel;
      return statusMatch && channelMatch;
    });
  }, [contentFilters, contentItems]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [overviewRes, campaignsRes, contentRes, settingsRes] = await Promise.all([
        getMarketingOverview(),
        getMarketingCampaigns(),
        getMarketingContent(),
        getMarketingAiSettings(),
      ]);
      setOverview(overviewRes.data);
      setCampaigns(campaignsRes.data || []);
      setContentItems(contentRes.data || []);
      setAiSettings(settingsRes.data);
      setSettingsForm({
        model: settingsRes.data.model,
        effort: settingsRes.data.effort,
        ...settingsRes.data.brand_kit,
        approved_claims: (settingsRes.data.brand_kit?.approved_claims || []).join('\n'),
        banned_phrases: (settingsRes.data.brand_kit?.banned_phrases || []).join('\n'),
      });
    } catch (error) {
      console.error(error);
      toast.error('Marketing workspace could not load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading) loadData();
  }, [authLoading, loadData]);

  const updateGeneratorField = (field, value) => {
    setGeneratorForm((current) => ({ ...current, [field]: value }));
  };

  const updateCampaignField = (field, value) => {
    setCampaignForm((current) => ({ ...current, [field]: value }));
  };

  const handleGenerate = async () => {
    if (!generatorForm.campaign_name.trim() || !generatorForm.program.trim()) {
      toast.error('Campaign and program are required');
      return;
    }
    try {
      setBusy(true);
      const res = await generateMarketingContent(generatorForm);
      setGenerated(res.data);
      setActiveTab('generated');
      toast.success('AI marketing package generated');
    } catch (error) {
      console.error(error);
      toast.error('Generation failed');
    } finally {
      setBusy(false);
    }
  };

  const handleSaveGenerated = async () => {
    if (!generated?.content?.length) {
      toast.error('Generate content first');
      return;
    }
    try {
      setBusy(true);
      const campaignRes = await createMarketingCampaign(generated.campaign);
      const campaignId = campaignRes.data.id;
      const drafts = generated.content.map((item) => ({ ...item, campaign_id: campaignId, status: 'draft' }));
      await bulkCreateMarketingContent(drafts);
      toast.success('Campaign and drafts saved');
      setActiveTab('library');
      await loadData();
    } catch (error) {
      console.error(error);
      toast.error('Could not save generated package');
    } finally {
      setBusy(false);
    }
  };

  const updateSettingsField = (field, value) => {
    setSettingsForm((current) => ({ ...current, [field]: value }));
  };

  const handleSaveSettings = async () => {
    if (!settingsForm) return;
    try {
      setBusy(true);
      const { model, effort, ...brandKit } = settingsForm;
      const { data } = await updateMarketingAiSettings({ model, effort, brand_kit: brandKit });
      setAiSettings(data);
      setSettingsForm({
        model: data.model,
        effort: data.effort,
        ...data.brand_kit,
        approved_claims: (data.brand_kit?.approved_claims || []).join('\n'),
        banned_phrases: (data.brand_kit?.banned_phrases || []).join('\n'),
      });
      toast.success('Generation settings saved');
    } catch (error) {
      console.error(error);
      toast.error('Could not save generation settings');
    } finally {
      setBusy(false);
    }
  };

  const handleCreateCampaign = async () => {
    if (!campaignForm.name.trim()) {
      toast.error('Campaign name is required');
      return;
    }
    try {
      setBusy(true);
      await createMarketingCampaign(campaignForm);
      setCampaignForm(buildDefaultCampaignForm());
      toast.success('Campaign created');
      await loadData();
    } catch (error) {
      console.error(error);
      toast.error('Could not create campaign');
    } finally {
      setBusy(false);
    }
  };

  const handleGenerateCampaignPosts = async () => {
    if (!postForm.campaign_id) {
      toast.error('Select a campaign');
      return;
    }
    try {
      setBusy(true);
      const res = await generateCampaignPosts(postForm.campaign_id, {
        ...postForm,
        post_count: Number(postForm.post_count),
      });
      toast.success(`${res.data.created} posts created for review`);
      setActiveTab('library');
      setContentFilters({ status: 'review', channel: 'all' });
      await loadData();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Could not generate campaign posts');
    } finally {
      setBusy(false);
    }
  };

  const handleCampaignStatus = async (campaign, status) => {
    try {
      await updateMarketingCampaign(campaign.id, { status });
      setCampaigns((current) => current.map((item) => (item.id === campaign.id ? { ...item, status } : item)));
      toast.success('Campaign updated');
      loadData();
    } catch (error) {
      console.error(error);
      toast.error('Could not update campaign');
    }
  };

  const handleDeleteCampaign = async (campaign) => {
    if (!window.confirm(`Delete campaign "${campaign.name}"?`)) return;
    try {
      await deleteMarketingCampaign(campaign.id);
      toast.success('Campaign deleted');
      await loadData();
    } catch (error) {
      console.error(error);
      toast.error('Could not delete campaign');
    }
  };

  const handleCopyContent = async (item) => {
    const text = [item.headline, item.body || item.caption, item.hashtags?.join(' ')].filter(Boolean).join('\n\n');
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied');
    } catch {
      toast.error('Clipboard unavailable');
    }
  };

  const handleQueueContent = async (item) => {
    const scheduledFor = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    try {
      await updateMarketingContent(item.id, { status: 'scheduled', scheduled_for: scheduledFor });
      toast.success('Queued for sharing');
      await loadData();
    } catch (error) {
      console.error(error);
      toast.error('Could not queue content');
    }
  };

  const handleShareContent = async (item) => {
    try {
      await shareMarketingContent(item.id);
      toast.success('Marked as shared');
      await loadData();
    } catch (error) {
      console.error(error);
      toast.error('Could not share content');
    }
  };

  const handleArchiveContent = async (item) => {
    try {
      await updateMarketingContent(item.id, { status: 'archived' });
      toast.success('Archived');
      await loadData();
    } catch (error) {
      console.error(error);
      toast.error('Could not archive content');
    }
  };

  const handleDeleteContent = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      await deleteMarketingContent(item.id);
      toast.success('Deleted');
      await loadData();
    } catch (error) {
      console.error(error);
      toast.error('Could not delete content');
    }
  };

  const totals = overview?.totals || {};

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
            AI Marketing
          </h2>
          <p className="mt-1 text-sm text-[#062B1A]/60">Campaigns, content, ads, and publishing queue</p>
        </div>
        <Button variant="outline" onClick={loadData} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        <StatCard label="Campaigns" value={totals.campaigns ?? 0} icon={Target} />
        <StatCard label="Active" value={totals.active_campaigns ?? 0} icon={Rocket} />
        <StatCard label="Drafts" value={totals.drafts ?? 0} icon={Wand2} />
        <StatCard label="Scheduled" value={totals.scheduled ?? 0} icon={CalendarClock} />
        <StatCard label="Shared" value={totals.shared ?? 0} icon={CheckCircle2} />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="h-auto flex-wrap justify-start bg-white p-1 shadow-sm">
          <TabsTrigger value="studio">AI Studio</TabsTrigger>
          <TabsTrigger value="generated">Generated</TabsTrigger>
          <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
          <TabsTrigger value="library">Content Queue</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="studio" className="space-y-6">
          <Card className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
                <Wand2 className="h-4 w-4 text-[#D4AF37]" />
                Create posts for an existing campaign
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-[#062B1A]/60">
                Creates a scheduled batch linked to the selected campaign and sends every post to Review before publishing.
              </p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div className="space-y-1.5 md:col-span-2">
                  <Label>Campaign</Label>
                  <Select value={postForm.campaign_id} onValueChange={(value) => setPostForm((current) => ({ ...current, campaign_id: value }))}>
                    <SelectTrigger><SelectValue placeholder="Select a campaign" /></SelectTrigger>
                    <SelectContent>
                      {campaigns.map((campaign) => (
                        <SelectItem key={campaign.id} value={campaign.id}>{campaign.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Number of posts</Label>
                  <Input type="number" min="1" max="30" value={postForm.post_count} onChange={(event) => setPostForm((current) => ({ ...current, post_count: event.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label>Media</Label>
                  <Select value={postForm.media_type} onValueChange={(value) => setPostForm((current) => ({ ...current, media_type: value }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="image">Images</SelectItem>
                      <SelectItem value="video">Videos</SelectItem>
                      <SelectItem value="mixed">Mixed</SelectItem>
                      <SelectItem value="none">Copy only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Tone</Label>
                  <Select value={postForm.tone} onValueChange={(value) => setPostForm((current) => ({ ...current, tone: value }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TONES.map((tone) => <SelectItem key={tone.value} value={tone.value}>{tone.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Offer</Label>
                  <Input value={postForm.offer} onChange={(event) => setPostForm((current) => ({ ...current, offer: event.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label>Location</Label>
                  <Input value={postForm.location} onChange={(event) => setPostForm((current) => ({ ...current, location: event.target.value }))} />
                </div>
                <div className="flex items-end">
                  <Button className="w-full" onClick={handleGenerateCampaignPosts} disabled={busy || campaigns.length === 0}>
                    <Sparkles className="h-4 w-4" />
                    Create for review
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.25fr_0.75fr]">
            <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
                  <Sparkles className="h-4 w-4 text-[#D4AF37]" />
                  AI Generator
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Campaign</Label>
                    <Input value={generatorForm.campaign_name} onChange={(event) => updateGeneratorField('campaign_name', event.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Program</Label>
                    <Input value={generatorForm.program} onChange={(event) => updateGeneratorField('program', event.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Audience</Label>
                    <Input value={generatorForm.audience} onChange={(event) => updateGeneratorField('audience', event.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Location</Label>
                    <Input value={generatorForm.location} onChange={(event) => updateGeneratorField('location', event.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Objective</Label>
                    <Select value={generatorForm.objective} onValueChange={(value) => updateGeneratorField('objective', value)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {OBJECTIVES.map((objective) => (
                          <SelectItem key={objective.value} value={objective.value}>{objective.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Tone</Label>
                    <Select value={generatorForm.tone} onValueChange={(value) => updateGeneratorField('tone', value)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TONES.map((tone) => (
                          <SelectItem key={tone.value} value={tone.value}>{tone.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5 md:col-span-2">
                    <Label>Offer</Label>
                    <Input value={generatorForm.offer} onChange={(event) => updateGeneratorField('offer', event.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Ad budget</Label>
                    <Input
                      type="number"
                      min="0"
                      value={generatorForm.budget}
                      onChange={(event) => updateGeneratorField('budget', event.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Channels</Label>
                  <ChannelPicker value={generatorForm.channels} onChange={(value) => updateGeneratorField('channels', value)} />
                </div>
                <div className="flex justify-end">
                  <Button onClick={handleGenerate} disabled={busy}>
                    <Wand2 className="h-4 w-4" />
                    Generate
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
                  <Plus className="h-4 w-4 text-[#D4AF37]" />
                  Campaign
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Name</Label>
                  <Input value={campaignForm.name} onChange={(event) => updateCampaignField('name', event.target.value)} />
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-1">
                  <div className="space-y-1.5">
                    <Label>Objective</Label>
                    <Select value={campaignForm.objective} onValueChange={(value) => updateCampaignField('objective', value)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {OBJECTIVES.map((objective) => (
                          <SelectItem key={objective.value} value={objective.value}>{objective.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Status</Label>
                    <Select value={campaignForm.status} onValueChange={(value) => updateCampaignField('status', value)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CAMPAIGN_STATUSES.map((status) => (
                          <SelectItem key={status} value={status}>{titleCase(status)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Audience</Label>
                  <Input value={campaignForm.audience} onChange={(event) => updateCampaignField('audience', event.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Program</Label>
                  <Input value={campaignForm.program} onChange={(event) => updateCampaignField('program', event.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Budget</Label>
                  <Input type="number" min="0" value={campaignForm.budget} onChange={(event) => updateCampaignField('budget', event.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Channels</Label>
                  <ChannelPicker value={campaignForm.channels} onChange={(value) => updateCampaignField('channels', value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Notes</Label>
                  <Textarea value={campaignForm.notes} onChange={(event) => updateCampaignField('notes', event.target.value)} className="min-h-20" />
                </div>
                <Button className="w-full" onClick={handleCreateCampaign} disabled={busy}>
                  <Plus className="h-4 w-4" />
                  Create
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="generated" className="space-y-6">
          {!generated ? (
            <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
              <CardContent className="p-10 text-center">
                <Sparkles className="mx-auto h-10 w-10 text-[#062B1A]/20" />
                <p className="mt-3 text-sm text-[#062B1A]/50">No generated package yet</p>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
                    {generated.campaign?.name}
                  </h3>
                  <p className="text-sm text-[#062B1A]/55">{generated.strategy?.positioning}</p>
                </div>
                <Button onClick={handleSaveGenerated} disabled={busy}>
                  <Rocket className="h-4 w-4" />
                  Save Drafts
                </Button>
              </div>

              <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm xl:col-span-2">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>Strategy</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm leading-6 text-[#062B1A]/65">{generated.strategy?.message}</p>
                    <div className="flex flex-wrap gap-2">
                      {(generated.strategy?.benefits || []).map((benefit) => (
                        <Badge key={benefit} variant="outline" className="border-[#062B1A]/10 bg-[#F7F8FA] text-[#062B1A]/70">
                          {benefit}
                        </Badge>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(generated.strategy?.seo_keywords || []).map((keyword) => (
                        <span key={keyword} className="rounded-full bg-[#062B1A]/5 px-3 py-1 text-xs font-medium text-[#062B1A]/60">
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>Audience</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {(generated.audience_segments || []).map((segment) => (
                      <div key={segment.name} className="rounded-lg border border-[#062B1A]/10 p-3">
                        <p className="text-sm font-semibold text-[#062B1A]">{segment.name}</p>
                        <p className="mt-1 text-xs text-[#062B1A]/55">{segment.intent}</p>
                        <p className="mt-2 text-xs font-medium text-[#062B1A]/65">{segment.message}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                {(generated.ad_plan || []).map((ad) => (
                  <Card key={`${ad.channel}-${ad.platform}`} className="border border-[rgba(6,43,26,0.08)] shadow-sm">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-[#062B1A]">{ad.platform}</p>
                          <p className="mt-1 text-xs text-[#062B1A]/50">{titleCase(ad.objective)}</p>
                        </div>
                        <Badge variant="outline" className="border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#8A6A12]">
                          {formatMoney(ad.budget)}
                        </Badge>
                      </div>
                      <p className="mt-3 text-sm leading-6 text-[#062B1A]/65">{ad.recommendation}</p>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {(ad.creative_angles || []).map((angle) => (
                          <span key={angle} className="rounded-full bg-[#F7F8FA] px-2.5 py-1 text-xs text-[#062B1A]/55">
                            {angle}
                          </span>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.2fr_0.8fr]">
                <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
                      <CalendarClock className="h-4 w-4 text-[#D4AF37]" />
                      Calendar
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {(generated.calendar || []).map((item) => (
                      <div key={`${item.day}-${item.channel}`} className="grid gap-2 rounded-lg border border-[#062B1A]/10 p-3 md:grid-cols-[100px_120px_1fr]">
                        <span className="text-sm font-semibold text-[#062B1A]">{item.day}</span>
                        <span className="text-sm text-[#062B1A]/55">{item.channel}</span>
                        <span className="text-sm text-[#062B1A]/65">{item.action}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>Review</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {(generated.strategy?.compliance_notes || []).map((note) => (
                      <div key={note} className="rounded-lg border border-[#062B1A]/10 bg-[#F7F8FA] p-3 text-sm leading-6 text-[#062B1A]/65">
                        {note}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-3">
                {(generated.content || []).map((item, index) => (
                  <Card key={`${item.channel}-${index}`} className="border border-[rgba(6,43,26,0.08)] shadow-sm">
                    <CardContent className="p-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <ChannelBadge channel={item.channel} />
                        <Badge variant="outline" className="border-[#062B1A]/10 bg-[#F7F8FA] text-[#062B1A]/60">
                          {item.cta}
                        </Badge>
                      </div>
                      <h3 className="mt-3 text-base font-semibold text-[#062B1A]">{item.title}</h3>
                      {item.headline && <p className="mt-1 text-sm font-medium text-[#062B1A]/75">{item.headline}</p>}
                      <p className="mt-3 whitespace-pre-line text-sm leading-6 text-[#062B1A]/65">{item.body || item.caption}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="campaigns" className="space-y-4">
          {campaigns.length === 0 ? (
            <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
              <CardContent className="p-10 text-center">
                <Target className="mx-auto h-10 w-10 text-[#062B1A]/20" />
                <p className="mt-3 text-sm text-[#062B1A]/50">No campaigns yet</p>
              </CardContent>
            </Card>
          ) : (
            campaigns.map((campaign) => (
              <Card key={campaign.id} className="border border-[rgba(6,43,26,0.08)] shadow-sm">
                <CardContent className="p-4">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className={statusBadgeClass(campaign.status)}>{titleCase(campaign.status)}</Badge>
                        <Badge variant="outline" className="border-[#062B1A]/10 bg-white text-[#062B1A]/60">
                          {OBJECTIVES.find((item) => item.value === campaign.objective)?.label || titleCase(campaign.objective)}
                        </Badge>
                        <span className="text-xs font-medium text-[#062B1A]/45">{formatMoney(campaign.budget)}</span>
                      </div>
                      <h3 className="mt-3 text-base font-semibold text-[#062B1A]">{campaign.name}</h3>
                      <p className="mt-1 text-sm text-[#062B1A]/60">{campaign.audience}</p>
                      {campaign.program && <p className="mt-1 text-sm font-medium text-[#062B1A]/70">{campaign.program}</p>}
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {(campaign.channels || []).map((channel) => (
                          <ChannelBadge key={channel} channel={channel} />
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 lg:justify-end">
                      <Select value={campaign.status} onValueChange={(value) => handleCampaignStatus(campaign, value)}>
                        <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {CAMPAIGN_STATUSES.map((status) => (
                            <SelectItem key={status} value={status}>{titleCase(status)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button variant="ghost" size="icon" className="h-9 w-9 text-red-500" onClick={() => handleDeleteCampaign(campaign)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="library" className="space-y-4">
          <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
            <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#062B1A]">
                <BarChart3 className="h-4 w-4 text-[#D4AF37]" />
                {filteredContent.length} items
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Select value={contentFilters.status} onValueChange={(value) => setContentFilters((current) => ({ ...current, status: value }))}>
                  <SelectTrigger className="w-full sm:w-40"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    {CONTENT_STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>{titleCase(status)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={contentFilters.channel} onValueChange={(value) => setContentFilters((current) => ({ ...current, channel: value }))}>
                  <SelectTrigger className="w-full sm:w-44"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All channels</SelectItem>
                    {CHANNELS.map((channel) => (
                      <SelectItem key={channel.id} value={channel.id}>{channel.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((item) => (
                <div key={item} className="h-36 animate-pulse rounded-[20px] bg-white" />
              ))}
            </div>
          ) : filteredContent.length === 0 ? (
            <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
              <CardContent className="p-10 text-center">
                <Mail className="mx-auto h-10 w-10 text-[#062B1A]/20" />
                <p className="mt-3 text-sm text-[#062B1A]/50">No content in this view</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredContent.map((item) => (
                <ContentPreview
                  key={item.id}
                  item={item}
                  campaignName={campaignMap[item.campaign_id]}
                  onCopy={handleCopyContent}
                  onQueue={handleQueueContent}
                  onShare={handleShareContent}
                  onArchive={handleArchiveContent}
                  onDelete={handleDeleteContent}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="settings" className="space-y-6">
          {!settingsForm ? (
            <Card className="border border-[#062B1A]/10 shadow-sm">
              <CardContent className="py-10 text-center text-sm text-[#062B1A]/55">Loading settings...</CardContent>
            </Card>
          ) : (
            <>
              {!aiSettings?.configured && (
                <Card className="border border-amber-300 bg-amber-50 shadow-sm">
                  <CardContent className="flex items-start gap-3 py-4">
                    <Settings2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
                    <div className="text-sm text-amber-900">
                      <p className="font-medium">Generation is not switched on yet.</p>
                      <p className="mt-1 text-amber-800/80">
                        The server needs an Anthropic API key before the AI Studio can write anything. You can still set
                        the model and brand voice here &mdash; they take effect as soon as the key is added.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card className="border border-[#062B1A]/10 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
                    <Sparkles className="h-4 w-4 text-[#D4AF37]" />
                    Which model writes the copy
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Model</Label>
                      <Select value={settingsForm.model} onValueChange={(value) => updateSettingsField('model', value)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {(aiSettings?.model_catalog || []).map((entry) => (
                            <SelectItem key={entry.id} value={entry.id}>{entry.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs leading-5 text-[#062B1A]/55">{activeModel?.note}</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Effort</Label>
                      <Select
                        value={settingsForm.effort}
                        onValueChange={(value) => updateSettingsField('effort', value)}
                        disabled={!activeModel?.supports_effort}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {(aiSettings?.effort_levels || []).map((level) => (
                            <SelectItem key={level} value={level}>{titleCase(level)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs leading-5 text-[#062B1A]/55">
                        {activeModel?.supports_effort
                          ? 'Higher effort means more considered copy and a higher cost per campaign.'
                          : 'This model has no effort setting.'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-[#062B1A]/10 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
                    How MCC sounds
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-[#062B1A]/60">
                    This goes into every generation. Keep it factual &mdash; anything written here is treated as approved.
                  </p>
                  <div className="space-y-2">
                    <Label>Voice</Label>
                    <Textarea
                      className="min-h-24"
                      value={settingsForm.voice || ''}
                      onChange={(event) => updateSettingsField('voice', event.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Who we are writing to</Label>
                    <Textarea
                      className="min-h-20"
                      value={settingsForm.audience_note || ''}
                      onChange={(event) => updateSettingsField('audience_note', event.target.value)}
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Claims we may make</Label>
                      <Textarea
                        className="min-h-28"
                        value={settingsForm.approved_claims || ''}
                        onChange={(event) => updateSettingsField('approved_claims', event.target.value)}
                      />
                      <p className="text-xs text-[#062B1A]/55">One per line. The model may rephrase these, never go beyond them.</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Phrases never to use</Label>
                      <Textarea
                        className="min-h-28"
                        value={settingsForm.banned_phrases || ''}
                        onChange={(event) => updateSettingsField('banned_phrases', event.target.value)}
                      />
                      <p className="text-xs text-[#062B1A]/55">One per line. Guarantees about jobs, visas or immigration belong here.</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>
                      Standing instructions <span className="font-normal text-[#062B1A]/45">(optional)</span>
                    </Label>
                    <Textarea
                      className="min-h-20"
                      value={settingsForm.notes || ''}
                      onChange={(event) => updateSettingsField('notes', event.target.value)}
                      placeholder="e.g. Do not mention the downtown campus this term."
                    />
                  </div>
                </CardContent>
              </Card>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs text-[#062B1A]/50">
                  {aiSettings?.updated_at
                    ? `Last changed ${formatDate(aiSettings.updated_at)}${aiSettings.updated_by_name ? ` by ${aiSettings.updated_by_name}` : ''}`
                    : 'Never changed from the defaults.'}
                </p>
                <Button onClick={handleSaveSettings} disabled={busy} className="bg-[#062B1A] text-white hover:bg-[#0A3B24]">
                  {busy ? 'Saving...' : 'Save settings'}
                </Button>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
